/** Figma: small line/markers (sub-pixel width/height from design export). */
export const PERFORMANCE_CHART_SMALL_DOT_WIDTH_PX = 4.909842014312744;
export const PERFORMANCE_CHART_SMALL_DOT_HEIGHT_PX = 4.907360076904297;
/** Chart.js `pointRadius` is half the visual diameter; average width/height for a circle. */
export const PERFORMANCE_CHART_SMALL_DOT_RADIUS_PX =
  (PERFORMANCE_CHART_SMALL_DOT_WIDTH_PX +
    PERFORMANCE_CHART_SMALL_DOT_HEIGHT_PX) /
  4;

/** Figma: expanded vertical bar — main value indicator dot. */
export const PERFORMANCE_CHART_EXPANDED_VALUE_DOT_PX = 24;

/**
 * Evenly spaced indices from 0 … n−1 (inclusive ends), at most `maxItems` entries.
 * Used for Figma-style x labels, vertical grid lines, and visible line markers when n is large.
 */
export function getSubsampledIndices(n, maxItems = 14) {
  if (n <= 0) return [];
  if (n <= maxItems) {
    return [...Array(n).keys()];
  }
  const step = Math.ceil((n - 1) / (maxItems - 1));
  const indices = [];
  for (let i = 0; i < n; i += step) indices.push(i);
  if (indices[indices.length - 1] !== n - 1) indices.push(n - 1);
  return indices;
}

/**
 * Time buckets for cc-case-stage-process-report formattedReport: 0–24h with half-hour slots.
 * Order: 0, 0.5, 1, 1.5, …, 23.5, 24
 */
export function orderedTimeBucketKeys() {
  const keys = [];
  for (let n = 0; n < 24; n += 1) {
    keys.push(String(n));
    keys.push(String(n + 0.5));
  }
  keys.push("24");
  return keys;
}

function bucketValue(formattedReport, key) {
  const entry = formattedReport?.[key];
  if (!entry || typeof entry !== "object") return 0;
  const v = entry.totalCompletedStage;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Slot keys present in the API (`"10"`, `"10.5"`, …), sorted by time. Ignores rollups and
 * non-bucket fields on `formattedReport`.
 */
export function extractSortedTimeBucketKeys(formattedReport) {
  if (!formattedReport || typeof formattedReport !== "object") return [];
  const out = [];
  for (const k of Object.keys(formattedReport)) {
    const entry = formattedReport[k];
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    if (entry.totalCompletedStage == null) continue;
    const numKey = Number(k);
    if (!Number.isFinite(numKey)) continue;
    out.push(k);
  }
  out.sort((a, b) => Number(a) - Number(b));
  return out;
}

/**
 * Plot only buckets returned by the API (chronological). xₙ = 0…N−1; cumulativeXₙ = running sum.
 * When no slot keys exist, `lastCumulativeX` falls back to `formattedReport.totalCompletedStage` if set.
 */
export function buildPerformanceChartSeries(formattedReport) {
  const keys = extractSortedTimeBucketKeys(formattedReport);
  let cum = 0;
  const ys = [];
  const points = [];

  keys.forEach((key, slotIndex) => {
    const y = bucketValue(formattedReport, key);
    cum += y;
    ys.push(y);
    points.push({ x: slotIndex, y, key, cumulativeX: cum });
  });

  if (
    points.length === 0 &&
    formattedReport &&
    typeof formattedReport === "object"
  ) {
    const rollup = formattedReport.totalCompletedStage;
    const n = Number(rollup);
    if (Number.isFinite(n)) cum = n;
  }

  return {
    keys,
    ys,
    points,
    lastCumulativeX: cum,
  };
}

/**
 * Flat series for empty chart state so grid, baseline, and cumulative x-labels still match the live layout.
 */
export function buildPlaceholderPerformancePoints(slotCount = 20) {
  const count = Math.max(6, Math.min(49, slotCount));
  const points = [];
  let cum = 0;
  for (let i = 0; i < count; i += 1) {
    cum += i === 0 ? 0 : 40;
    points.push({
      x: i,
      y: 0,
      key: String(i),
      cumulativeX: cum,
    });
  }
  return points;
}

/**
 * Total Task pill: raw remainder from `actual mod expected` (same units as actual/expected).
 * remainder = ((actual % expected) + expected) % expected (stable for negative actual).
 * UI shows this value with a % suffix as "task done %".
 */
export function taskDoneRemainderFromMod(
  actualCumulative,
  totalExpectedStages,
) {
  const a = Number(actualCumulative);
  const e = Number(totalExpectedStages);
  if (!Number.isFinite(e) || e <= 0) return null;
  if (!Number.isFinite(a)) return null;
  return ((a % e) + e) % e;
}

/**
 * API nests `totalExpectedStages` and rollup `totalCompletedStage` under `formattedReport`
 * (not on the response root). Prefer those for the pill; fall back to summed series.
 */
export function resolveActualAndExpectedForTaskPercent(payload) {
  const fr = payload?.formattedReport ?? {};

  const actual = Number(fr.totalCompletedStage ?? 0);
  const expected = Number(fr.totalExpectedStagesAsPerWorkedHours ?? 0);

  let percent = 0;

  if (expected > 0) {
    percent = (actual / expected) * 100 - 100;
  }

  return {
    actual,
    expected,
    percent,
    displayTotal: actual,
  };
}

export function formatExpandedHourStartIST(hourKey) {
  const slotHour = Number(hourKey);

  if (!Number.isFinite(slotHour)) return "";

  // convert slot hour → minutes
  const utcMinutes = slotHour * 60;

  // add IST offset (5h 30m)
  const istMinutes = utcMinutes + 330;

  let hours = Math.floor(istMinutes / 60) % 24;
  const minutes = istMinutes % 60;

  const suffix = hours >= 12 ? "pm" : "am";

  let displayHour = hours % 12;
  if (displayHour === 0) displayHour = 12;

  return `${displayHour}:${String(minutes).padStart(2, "0")} ${suffix}`;
}

export function formatTotalTaskDisplay(n) {
  if (!Number.isFinite(n)) return "—";
  try {
    return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  } catch {
    return String(n);
  }
}

/** Cumulative x under each vertical grid line (Figma-style: 0, 150, 260, …). */
export function formatXAxisLabel(x) {
  if (!Number.isFinite(x)) return "";
  const r = Math.round(x);
  if (Math.abs(x - r) < 1e-6) {
    if (Math.abs(r) >= 10000) return r.toLocaleString("en-IN");
    return String(r);
  }
  if (x >= 1000) {
    return x.toLocaleString("en-IN", { maximumFractionDigits: 1 });
  }
  return String(Number(x.toFixed(1)));
}

/**
 * `formattedReport` slot key: hour or half-hour as string ("0" … "23.5", "24").
 * Returns 24h clock text (e.g. 00:00 … 24:00).
 */
export function formatSlotTimeLabel(key) {
  const h = Number(key);
  if (!Number.isFinite(h)) return "";
  if (h >= 24 - 1e-9) return "24:00";
  const hours = Math.floor(h);
  const minutes = Math.round((h - hours) * 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/** Indices of full-hour slots (keys "0","1",…,"24") for 0.5h-step series: 0, 2, 4, …, last even index. */
export function getHourlySlotIndices(pointCount) {
  if (pointCount <= 0) return [];
  const out = [];
  for (let i = 0; i < pointCount; i += 2) out.push(i);
  return out;
}

/** Slot key is a whole hour on the 0–24 grid ("0"…"23", "24"), not a half-hour like "5.5". */
export function isIntegerHourSlotKey(key) {
  const n = Number(key);
  if (!Number.isFinite(n) || n < 0 || n > 24) return false;
  return Math.abs(n - Math.round(n)) < 1e-9;
}

/** Label index 0…23 for expanded bars (keys "23" and "24" both map to the last hour range). */
export function expandedHourIndexFromIntegerSlotKey(key) {
  const n = Number(key);
  if (!Number.isFinite(n)) return 0;
  if (n >= 23) return 23;
  return Math.min(23, Math.max(0, Math.round(n)));
}

/**
 * Same raw bucket totals as `buildPerformanceChartSeries` / upper line chart, aggregated to clock hours.
 * - Hours 0–22: sum of half-hour keys `"h"` + `"h.5"`.
 * - Last bar (hour ending day): keys `"23"`, `"23.5"`, and `"24"` (matches 49 half-hour slots → 24 hours).
 * @returns {number[]} length 24
 */
export function aggregateHalfHourSeriesToHourly(keys, ys) {
  if (!Array.isArray(keys) || !Array.isArray(ys) || keys.length !== ys.length) {
    return Array(24).fill(0);
  }
  const map = new Map();
  keys.forEach((k, i) => {
    map.set(String(k), Number(ys[i]) || 0);
  });
  const out = [];
  for (let h = 0; h < 23; h += 1) {
    out.push((map.get(String(h)) ?? 0) + (map.get(String(h + 0.5)) ?? 0));
  }
  out.push(
    (map.get("23") ?? 0) + (map.get("23.5") ?? 0) + (map.get("24") ?? 0),
  );
  return out;
}

/** Label for expanded hourly bar: local hour range (24 bars). */
export function formatExpandedHourRangeLabel(hourIndex) {
  if (!Number.isFinite(hourIndex) || hourIndex < 0 || hourIndex > 23) return "";
  if (hourIndex < 23) {
    const a = String(hourIndex).padStart(2, "0");
    const b = String(hourIndex + 1).padStart(2, "0");
    return `${a}:00–${b}:00`;
  }
  return "23:00–24:00";
}
